import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import { LoaderComponent } from "../../Common/LoaderComponent";
import { ChartOfAccountStyles } from "../Accounts/Chart-Of-Account/ChartOfAccountShared";

const isRole =
  (...types) =>
  (user) =>
    types.includes(user.user_type);

const isHrOrDepartmentHead = (user) =>
  isRole("admin", "hr")(user) || user.department_head == 1;

// Each report is shown only to the users its `can` check allows. A group with
// no visible reports is hidden. Reports without a `to` are not built yet.
const REPORT_GROUPS = [
  {
    key: "hr",
    title: "HR Management",
    subtitle: "People, payroll and attendance",
    icon: "fa-users",
    reports: [
      {
        to: "/monthly-team-summary",
        title: "Monthly Team Summary",
        description: "Attendance and leave for your team, month by month",
        icon: "fa-sitemap",
        can: isHrOrDepartmentHead,
      },
      {
        to: "/hr-salary-structure",
        title: "Salary Structure",
        description: "Pay components and grades per employee",
        icon: "fa-list-alt",
        can: isRole("admin", "hr"),
      },
      {
        to: "/hr-leave-report",
        title: "Leave Report",
        description: "Leave taken and remaining balances",
        icon: "fa-calendar-minus-o",
        can: isRole("admin", "hr"),
      },
      {
        to: "/monthly-salary-report",
        title: "Monthly Salary Report",
        description: "Salary sheet for a selected month",
        icon: "fa-file-text-o",
        can: isRole("admin", "hr"),
      },
      {
        to: "/attendance-report",
        title: "Attendance Report",
        description: "Daily presence, late entries and absences",
        icon: "fa-calendar-check-o",
        can: isRole("admin", "hr"),
      },
      {
        to: "/salary-payment-report",
        title: "Paid Salary Report",
        description: "Salary payments that have been made",
        icon: "fa-credit-card",
        can: isRole("admin", "hr"),
      },
      {
        to: "/apa-report",
        title: "APA Report",
        description: "Annual performance appraisal results",
        icon: "fa-line-chart",
        can: isRole("admin", "hr"),
      },
    ],
  },
  {
    key: "accounts",
    title: "Accounts",
    subtitle: "Ledgers, payables, receivables and balances",
    icon: "fa-calculator",
    reports: [
      {
        to: "/chart-of-account-ledger-report",
        title: "Chart Of Account Ledger",
        description: "Transactions and running balance per account",
        icon: "fa-book",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/sub-category-list",
        title: "Subcategory Ledger",
        description: "Ledger grouped by account subcategory",
        icon: "fa-folder-open-o",
        can: isRole("admin", "accounts"),
      },
      // {
      //   to: "/vendor-supplier-payable-report",
      //   title: "Vendor/Supplier Payable",
      //   description: "Amounts owed to vendors and suppliers",
      //   icon: "fa-arrow-circle-up",
      //   can: isRole("admin", "accounts"),
      // },
      {
        to: "/supplier-payable",
        title: "Supplier Payable",
        description: "Suppliers billed more than they have been paid, as at a date",
        icon: "fa-credit-card",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/reference-report",
        title: "Reference Report",
        description: "Every journal voucher reference by account: paid, left and each voucher day by day",
        icon: "fa-tags",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/ledger-wise-transactions-present-balance",
        title: "Ledger Wise Transactions & Present Balance",
        description: "Every supplier's PO value, debits, credits, balance and matured dues, as at a date",
        icon: "fa-book",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/distributor-payable-report",
        title: "Distributor Payable",
        description: "What we owe each distributor as at a date, with opening balance and entries",
        icon: "fa-arrow-circle-up",
        can: isRole("admin", "accounts"),
      },
      // {
      //   to: "/receipt-payment-summary-report",
      //   title: "Receipts & Payments Summary",
      //   description: "Cash and bank inflows against outflows",
      //   icon: "fa-exchange",
      //   can: isRole("admin", "accounts"),
      // },
      {
        to: "/receipts-payments-voucher-summary",
        title: "Receipts & Payments Voucher Summary",
        description: "Receipt and payment vouchers by head of account, per journal code",
        icon: "fa-list-alt",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/vendor-supplier-receivable-report",
        title: "Vendor/Supplier Receivable",
        description: "Amounts due from vendors and suppliers",
        icon: "fa-arrow-circle-down",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/supplier-advance",
        title: "Supplier Advance",
        description: "Suppliers paid more than they have billed, as at a date",
        icon: "fa-forward",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/distributor-receivable-report",
        title: "Distributor Receivable",
        description: "What each distributor owes as at a date, with opening due and entries",
        icon: "fa-arrow-circle-down",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/trial-balance",
        title: "Trial Balance",
        description: "Debit and credit totals for every account",
        icon: "fa-balance-scale",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/distributors-ps-within-date-range",
        title: "Distributors PS Within Date Range",
        description: "Primary sales amounts received from distributors in a period",
        icon: "fa-handshake-o",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/daily-sales-report-company-wise",
        title: "Daily Sales Report Company Wise",
        description: "Product sales against target, valued at a vendor's purchase rate",
        icon: "fa-shopping-cart",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/profit-and-loss",
        title: "Profit & Loss Account Statement",
        description: "Revenue, expenses and profit for a period",
        tag: "Income Statement",
        icon: "fa-area-chart",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/cash-flow-statement",
        title: "Cash Flow Statement",
        description: "Cash in and out by operating, investing and financing activity",
        icon: "fa-exchange",
        can: isRole("admin", "accounts"),
      },
      {
        to: "/statement-of-financial-position",
        title: "Statement of Financial Position",
        description: "Balance sheet: assets, equity and liabilities at a date",
        tag: "Balance Sheet",
        icon: "fa-university",
        can: isRole("admin", "accounts"),
      },
    ],
  },
  {
    key: "depot",
    title: "Depot",
    subtitle: "Stock held at depots and their distributors",
    icon: "fa-building-o",
    reports: [
      {
        to: "/report/depo-wise-raw-material-stock",
        title: "Raw Material Stock",
        description: "Raw material on hand, depot by depot",
        icon: "fa-cubes",
        can: isRole("supply_chain"),
      },
      {
        to: "/report/depo-wise-packing-material-stock",
        title: "Packing Material Stock",
        description: "Packing material on hand, depot by depot",
        icon: "fa-archive",
        can: isRole("supply_chain"),
      },
      {
        to: "/report/depo-wise-product-stock",
        title: "Finished Good Stock",
        description: "Finished products on hand, depot by depot",
        icon: "fa-cube",
        can: isRole("supply_chain"),
      },
      {
        to: "/report/product-stock",
        title: "Product Stock",
        description: "Finished products held at your depot",
        icon: "fa-cube",
        can: isRole("depot"),
      },
      {
        to: "/report/distributor",
        title: "Distributors",
        description: "Distributors and their details",
        icon: "fa-truck",
        can: isRole("depot", "supply_chain"),
      },
    ],
  },
  {
    key: "sales",
    title: "Sales",
    subtitle: "Sales performance by SKU",
    icon: "fa-bar-chart",
    reports: [
      {
        title: "Monthly End SKU wise Sales",
        description: "Sales per SKU by payment date",
        tag: "Payment Date Wise",
        icon: "fa-money",
        can: isRole("supply_chain"),
      },
      {
        title: "Monthly End SKU wise Sales",
        description: "Sales per SKU by delivery date",
        tag: "Delivery Date Wise",
        icon: "fa-truck",
        can: isRole("supply_chain"),
      },
    ],
  },
];

const ReportStyles = () => (
  <style>
    {`
      .rpt-search {
        position: relative;
        min-width: 260px;
      }
      .rpt-search > i {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        color: #b6bacb;
        pointer-events: none;
      }
      .rpt-search .form-control {
        padding-left: 34px;
        padding-right: 32px;
        border-radius: 8px;
      }
      .rpt-search .form-control:focus {
        border-color: #2a2962;
        box-shadow: 0 0 0 0.2rem rgba(42, 41, 98, 0.12);
      }
      .rpt-search-clear {
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        border: 0;
        background: none;
        color: #8b90a5;
        padding: 2px 6px;
        cursor: pointer;
      }

      .rpt-group-title {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .rpt-group-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 8px;
        background: #2a2962;
        color: #fff;
        font-size: 0.95rem;
        flex-shrink: 0;
      }
      .rpt-group-sub {
        display: block;
        font-size: 0.74rem;
        font-weight: 500;
        color: #8b90a5;
      }
      .rpt-count {
        padding: 3px 10px;
        border-radius: 12px;
        background: #f2f3f9;
        color: #52566b;
        font-size: 0.72rem;
        font-weight: 600;
        white-space: nowrap;
      }

      .rpt-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 12px;
      }
      .rpt-tile {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        border: 1px solid #e6e8f0;
        border-radius: 8px;
        background: #fff;
        color: inherit;
        text-decoration: none;
        transition: border-color 0.15s ease, box-shadow 0.15s ease,
          transform 0.15s ease;
      }
      a.rpt-tile:hover,
      a.rpt-tile:focus-visible {
        border-color: #2a2962;
        box-shadow: 0 4px 12px rgba(42, 41, 98, 0.1);
        transform: translateY(-1px);
        text-decoration: none;
        outline: none;
      }
      .rpt-tile-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 8px;
        background: #eef0f8;
        color: #2a2962;
        font-size: 1rem;
        flex-shrink: 0;
        transition: background 0.15s ease, color 0.15s ease;
      }
      a.rpt-tile:hover .rpt-tile-icon {
        background: #2a2962;
        color: #fff;
      }
      .rpt-tile-text { flex: 1; min-width: 0; }
      .rpt-tile-title {
        display: block;
        font-size: 0.88rem;
        font-weight: 600;
        color: #2a2962;
        line-height: 1.3;
      }
      .rpt-tile-desc {
        display: block;
        margin-top: 2px;
        font-size: 0.74rem;
        color: #8b90a5;
        line-height: 1.4;
      }
      .rpt-tile-tag {
        display: inline-block;
        margin-top: 5px;
        padding: 1px 8px;
        border-radius: 10px;
        background: #f2f3f9;
        color: #52566b;
        font-size: 0.68rem;
        font-weight: 600;
      }
      .rpt-tile-arrow {
        color: #d7dae8;
        transition: color 0.15s ease, transform 0.15s ease;
      }
      a.rpt-tile:hover .rpt-tile-arrow {
        color: #e42320;
        transform: translateX(2px);
      }
      .rpt-tile.is-disabled {
        background: #fafbfd;
        cursor: not-allowed;
      }
      .rpt-tile.is-disabled .rpt-tile-icon {
        background: #f5f6fa;
        color: #b6bacb;
      }
      .rpt-tile.is-disabled .rpt-tile-title { color: #8b90a5; }
      .rpt-soon {
        padding: 2px 8px;
        border-radius: 10px;
        background: #fdf3e2;
        color: #c77700;
        font-size: 0.66rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
      }

      .rpt-empty {
        padding: 48px 20px;
        text-align: center;
        color: #8b90a5;
      }
      .rpt-empty i {
        display: block;
        margin-bottom: 12px;
        font-size: 2rem;
        color: #d7dae8;
      }
      .rpt-empty strong {
        display: block;
        margin-bottom: 4px;
        color: #2a2962;
        font-size: 0.95rem;
      }

      .rpt-skeleton {
        height: 68px;
        border-radius: 8px;
        background: linear-gradient(90deg, #f2f3f9 25%, #fafbfd 50%, #f2f3f9 75%);
        background-size: 200% 100%;
        animation: rpt-shimmer 1.2s ease-in-out infinite;
      }
      @keyframes rpt-shimmer {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
      }
    `}
  </style>
);

const ReportTile = ({ report }) => {
  const content = (
    <>
      <span className="rpt-tile-icon">
        <i className={`fa ${report.icon}`} aria-hidden="true"></i>
      </span>
      <span className="rpt-tile-text">
        <span className="rpt-tile-title">{report.title}</span>
        <span className="rpt-tile-desc">{report.description}</span>
        {report.tag && <span className="rpt-tile-tag">{report.tag}</span>}
      </span>
      {report.to ? (
        <i className="fa fa-angle-right rpt-tile-arrow" aria-hidden="true"></i>
      ) : (
        <span className="rpt-soon">Coming soon</span>
      )}
    </>
  );

  return report.to ? (
    <Link to={report.to} className="rpt-tile">
      {content}
    </Link>
  ) : (
    <div className="rpt-tile is-disabled" aria-disabled="true">
      {content}
    </div>
  );
};

const Reports = () => {
  const [receiveData, setReceiveData] = useState(null);
  const [search, setSearch] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/login") {
      fetchData();
    }
  }, [location.pathname]);

  const fetchData = () => {
    Http.get("/login-staff-detail")
      .then((res) => setReceiveData(res.data || {}))
      .catch((e) => {
        console.error("Fetch error:", e);
        setReceiveData({});
      });
  };

  const groups = useMemo(() => {
    if (!receiveData) {
      return [];
    }
    return REPORT_GROUPS.map((group) => ({
      ...group,
      reports: group.reports.filter((report) => report.can(receiveData)),
    })).filter((group) => group.reports.length);
  }, [receiveData]);

  const query = search.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!query) {
      return groups;
    }
    return groups
      .map((group) => {
        // A search on the group's name lists all of its reports.
        if (group.title.toLowerCase().includes(query)) {
          return group;
        }
        return {
          ...group,
          reports: group.reports.filter((report) =>
            [report.title, report.description, report.tag]
              .filter(Boolean)
              .some((text) => text.toLowerCase().includes(query)),
          ),
        };
      })
      .filter((group) => group.reports.length);
  }, [groups, query]);

  const totalReports = groups.reduce(
    (sum, group) => sum + group.reports.length,
    0,
  );
  const visibleReports = visibleGroups.reduce(
    (sum, group) => sum + group.reports.length,
    0,
  );

  if (location.pathname === "/" || location.pathname === "/login") return null;

  const loading = receiveData === null;

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <ReportStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Reports</h5>
            <p className="text-muted mb-0 tx-12">
              {loading
                ? "Loading the reports available to you"
                : `${totalReports} report${totalReports === 1 ? "" : "s"} available to you across ${groups.length} categor${groups.length === 1 ? "y" : "ies"}`}
            </p>
          </div>
          {totalReports > 0 && (
            <div className="rpt-search">
              <i className="fa fa-search" aria-hidden="true"></i>
              <input
                type="text"
                className="form-control"
                placeholder="Search reports"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search reports"
              />
              {search && (
                <button
                  type="button"
                  className="rpt-search-clear"
                  onClick={() => setSearch("")}
                  title="Clear search"
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="am-pagebody" style={{ minHeight: "80vh" }}>
          {loading && (
            <div className="coa-panel">
              <div className="coa-panel-body">
                <div className="rpt-grid">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="rpt-skeleton"></div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && totalReports === 0 && (
            <div className="coa-panel">
              <div className="rpt-empty">
                <i className="fa fa-lock" aria-hidden="true"></i>
                <strong>No reports available</strong>
                Your role does not have access to any reports. Contact an
                administrator if you need one.
              </div>
            </div>
          )}

          {!loading && totalReports > 0 && visibleReports === 0 && (
            <div className="coa-panel">
              <div className="rpt-empty">
                <i className="fa fa-search" aria-hidden="true"></i>
                <strong>No reports match &ldquo;{search.trim()}&rdquo;</strong>
                Try a different name, or{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => setSearch("")}
                >
                  show all reports
                </button>
                .
              </div>
            </div>
          )}

          {visibleGroups.map((group) => (
            <div className="coa-panel" key={group.key}>
              <div className="coa-panel-head">
                <span className="rpt-group-title">
                  <span className="rpt-group-icon">
                    <i className={`fa ${group.icon}`} aria-hidden="true"></i>
                  </span>
                  <span>
                    {group.title}
                    <span className="rpt-group-sub">{group.subtitle}</span>
                  </span>
                </span>
                <span className="rpt-count">
                  {group.reports.length} report
                  {group.reports.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="coa-panel-body">
                <div className="rpt-grid">
                  {group.reports.map((report) => (
                    <ReportTile
                      key={report.to || `${report.title}-${report.tag}`}
                      report={report}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;
